import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase, supabaseConfigured } from '@/lib/supabase';

const AuthContext = createContext(null);

// Hard upper bound for the auth bootstrap. If anything takes longer than this,
// we release the loading state so the app renders instead of staying blank.
const BOOTSTRAP_TIMEOUT_MS = 8000;
// Per-query timeout for Supabase REST calls during bootstrap.
const QUERY_TIMEOUT_MS = 6000;

function withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
    ),
  ]);
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [membership, setMembership] = useState(null); // { role, organisation_id }
  const [organisation, setOrganisation] = useState(null); // { id, name } loaded separately
  const [isExternal, setIsExternal] = useState(false); // CP-1 portal-only user (no organisation membership)
  const [loading, setLoading] = useState(true);
  const [bootstrapError, setBootstrapError] = useState(null);
  const [accessResolved, setAccessResolved] = useState(false); // membership + portal lookup finished (success or failure)
  const [accessError, setAccessError] = useState(null); // lookup failed/timed out → fail closed (no internal shell, no portal)
  const bootstrapDoneRef = useRef(false);

  const finishBootstrap = useCallback(() => {
    if (bootstrapDoneRef.current) return;
    bootstrapDoneRef.current = true;
    setLoading(false);
  }, []);

  // Fetch membership + organisation WITHOUT PostgREST embedded joins so a
  // missing FK or RLS on `organisations` cannot block the dashboard.
  const loadMembership = useCallback(async (currentUser) => {
    if (!currentUser || !supabase) {
      setMembership(null);
      setOrganisation(null);
      setIsExternal(false);
      setAccessError(null);
      setAccessResolved(true);
      return;
    }
    setAccessResolved(false);
    setAccessError(null);
    try {
      // eslint-disable-next-line no-console
      console.info('[Auth] loading membership for', currentUser.id);
      const { data: memData, error: memErr } = await withTimeout(
        supabase
          .from('organisation_members')
          .select('role, organisation_id')
          .eq('user_id', currentUser.id)
          .limit(1)
          .maybeSingle(),
        QUERY_TIMEOUT_MS,
        'organisation_members query'
      );

      if (memErr) {
        console.warn('[Auth] membership error', memErr);
        setBootstrapError(`organisation_members: ${memErr.message}`);
        setAccessError(`Could not verify your access (${memErr.message}).`);
        setMembership(null);
        setOrganisation(null);
        return;
      }

      setMembership(memData || null);
      setBootstrapError(null);

      if (!memData?.organisation_id) {
        setOrganisation(null);
        // CP-1: no internal membership → check for external portal access (bind by email, then look up)
        try {
          await withTimeout(supabase.rpc('cp1_claim_memberships'), QUERY_TIMEOUT_MS, 'claim');
          const { data: ext, error: extErr } = await withTimeout(supabase.from('v_portal_projects').select('id').limit(1), QUERY_TIMEOUT_MS, 'portal projects');
          if (extErr) throw extErr;
          setIsExternal(Boolean(ext?.length));
        } catch (e) { console.warn('[Auth] portal lookup', e); setIsExternal(false); setAccessError(`Could not verify your project access (${e?.message || 'lookup failed'}).`); }
        return;
      }
      setIsExternal(false);

      // Separate query for organisation details — non-fatal if blocked by RLS.
      try {
        const { data: orgData, error: orgErr } = await withTimeout(
          supabase
            .from('organisations')
            .select('id, name')
            .eq('id', memData.organisation_id)
            .limit(1)
            .maybeSingle(),
          QUERY_TIMEOUT_MS,
          'organisations query'
        );
        if (orgErr) {
          console.warn('[Auth] organisation lookup error', orgErr);
          setOrganisation(null);
        } else {
          setOrganisation(orgData || null);
        }
      } catch (e) {
        console.warn('[Auth] organisation lookup threw', e);
        setOrganisation(null);
      }
    } catch (e) {
      console.warn('[Auth] loadMembership threw', e);
      setBootstrapError(e?.message || 'Could not load organisation');
      setAccessError(`Could not verify your access (${e?.message || 'unknown error'}).`);
      setMembership(null);
      setOrganisation(null);
    } finally {
      setAccessResolved(true);
    }
  }, []);

  useEffect(() => {
    if (!supabaseConfigured) {
      finishBootstrap();
      return;
    }
    let mounted = true;

    // Absolute safety net — the dashboard must render within BOOTSTRAP_TIMEOUT_MS.
    const safetyTimer = setTimeout(() => {
      if (!bootstrapDoneRef.current) {
        console.warn('[Auth] bootstrap safety timeout fired');
        setBootstrapError((prev) => prev || 'Authentication bootstrap timed out.');
        finishBootstrap();
      }
    }, BOOTSTRAP_TIMEOUT_MS);

    (async () => {
      try {
        const { data, error } = await withTimeout(
          supabase.auth.getSession(),
          QUERY_TIMEOUT_MS,
          'auth.getSession'
        );
        if (!mounted) return;
        if (error) {
          console.warn('[Auth] getSession error', error);
          setBootstrapError(error.message || 'Could not read session');
        }
        const current = data?.session ?? null;
        setSession(current);
        setUser(current?.user ?? null);
        if (current?.user) {
          await loadMembership(current.user);
        }
      } catch (e) {
        console.warn('[Auth] getSession threw', e);
        setBootstrapError(e?.message || 'Could not initialise session');
      } finally {
        if (mounted) finishBootstrap();
      }
    })();

    // IMPORTANT: do NOT await DB queries inside the onAuthStateChange callback —
    // supabase-js serialises these and awaiting a DB call here can deadlock
    // subsequent auth events. Defer with setTimeout to break the chain.
    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;
      console.info('[Auth] onAuthStateChange', event);
      setSession(newSession ?? null);
      setUser(newSession?.user ?? null);

      if (newSession?.user) {
        setTimeout(() => {
          if (!mounted) return;
          loadMembership(newSession.user).finally(() => {
            if (mounted) finishBootstrap();
          });
        }, 0);
      } else {
        setMembership(null);
        setOrganisation(null);
        setIsExternal(false);
        setAccessError(null);
        setAccessResolved(true);
        finishBootstrap();
      }
    });

    return () => {
      mounted = false;
      clearTimeout(safetyTimer);
      sub?.subscription?.unsubscribe?.();
    };
  }, [loadMembership, finishBootstrap]);

  const signIn = useCallback(async (email, password) => {
    if (!supabase) throw new Error('Supabase is not configured');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    bootstrapDoneRef.current = false;
    setLoading(true);
    await supabase.auth.signOut();
    // onAuthStateChange will clear user and call finishBootstrap again.
  }, []);

  const sendPasswordReset = useCallback(async (email) => {
    if (!supabase) throw new Error('Supabase is not configured');
    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) throw error;
  }, []);

  const value = {
    session,
    user,
    membership,
    organisation,
    organisationId: membership?.organisation_id || null,
    role: membership?.role || null,
    isExternal,
    accessResolved,
    accessError,
    loading,
    bootstrapError,
    signIn,
    signOut,
    sendPasswordReset,
    isAuthenticated: Boolean(user),
    supabaseConfigured,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
